import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { macroService } from '../../services';
import { FaSearch, FaCheck, FaTimes, FaCrown } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import Card from '../../components/Card/Card';
import Button from '../../components/buttons/Button';

export default function MacroSearchWindow() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const isGoldOrEnterprise = useMemo(() => {
    return Boolean(
      profile?.isGoldOrEnterprise === true ||
      profile?.isgoldorenterprise === true ||
      profile?.subscription?.planType === 'gold' ||
      profile?.subscription?.planType === 'enterprise' ||
      profile?.subscription?.plan === 'gold' ||
      profile?.subscription?.plan === 'enterprise'
    );
  }, [profile]);
  const [macros, setMacros] = React.useState([]);
  const [query, setQuery] = React.useState('');
  const [highlight, setHighlight] = React.useState(0);
  const inputRef = React.useRef(null);

  const loadMacros = React.useCallback(async () => {
    try {
      const list = await window.electronAPI.getMacros();
      const valid = (list || []).filter(m => m.key && m.content);
      setMacros(valid);
      // Reset search and highlight when refreshing
      setQuery('');
      setHighlight(0);
    } catch (_e) {
      setMacros([]);
    }
  }, []);

  const refreshFromServer = React.useCallback(async () => {
    try {
      const serverList = await macroService.getMacros();
      const normalized = (serverList || [])
        .filter(m => m.keyword && m.expansionText)
        .map(m => ({ key: m.keyword, content: m.expansionText }));
      if (normalized.length) {
        setMacros(normalized);
        try { await window.electronAPI.saveMacros(normalized); } catch (_e) {}
      }
    } catch (_e) {
      // ignore background refresh errors
    }
  }, []);

  React.useEffect(() => {
    // Initial load
    loadMacros();
    // Trigger background refresh without blocking UI
    refreshFromServer();
    
    // Listen for refresh event from Electron
    const handleRefresh = () => {
      loadMacros();
      refreshFromServer();
    };
    
    if (window.electronAPI && window.electronAPI.on) {
      window.electronAPI.on('refresh-macros', handleRefresh);
      // Use capturing keydown for robustness in case focus is on input
      const onGlobalNavNext = () => setHighlight(h => Math.min(filtered.length - 1, h + 1));
      const onGlobalNavPrev = () => setHighlight(h => Math.max(0, h - 1));
      const onGlobalInsert = () => insertAtIndex(highlight);
      const onGlobalEscape = () => window.electronAPI.hideMacroSearch?.();
      window.electronAPI.on('navigate-next', onGlobalNavNext);
      window.electronAPI.on('navigate-previous', onGlobalNavPrev);
      window.electronAPI.on('insert-current-macro', onGlobalInsert);
      window.electronAPI.on('macro:escape', onGlobalEscape);
      // Store refs for cleanup
      window.__macroHandlers = { onGlobalNavNext, onGlobalNavPrev, onGlobalInsert, onGlobalEscape };
    }
    
    // Focus input on mount
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        try { inputRef.current.select(); } catch (_e) {}
      }
    }, 0);
    
    // Refocus when window regains focus or becomes visible
    const onFocus = () => {
      if (inputRef.current) {
        inputRef.current.focus();
        try { inputRef.current.select(); } catch (_e) {}
      }
      // Also refresh macros when window regains focus
      loadMacros();
    };
    window.addEventListener('focus', onFocus);
    const onVis = () => { 
      if (!document.hidden) {
        onFocus();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      // best-effort cleanup: no off helper available
      window.__macroHandlers = undefined;
    };
  }, [loadMacros, refreshFromServer]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return macros.slice(0, 50);
    return macros
      .map(m => {
        const k = (m.key || '').toLowerCase();
        const c = (m.content || '').toLowerCase();
        const keyIdx = k.indexOf(q);
        const contentIdx = c.indexOf(q);
        const matchesKey = keyIdx !== -1;
        const matchesContent = contentIdx !== -1;
        const primary = matchesKey ? 0 : 1; // prefer key matches
        const score = Math.min(matchesKey ? keyIdx : Infinity, matchesContent ? contentIdx : Infinity);
        return { ...m, _primary: primary, _score: score };
      })
      .filter(m => m._score !== Infinity)
      .sort((a, b) => (a._primary - b._primary) || (a._score - b._score) || a.key.localeCompare(b.key))
      .slice(0, 50);
  }, [macros, query]);

  React.useEffect(() => {
    if (highlight >= filtered.length) setHighlight(0);
  }, [filtered.length, highlight]);

  const insertAtIndex = React.useCallback(async (idx) => {
    if (idx < 0 || idx >= filtered.length) return;
    const m = filtered[idx];
    // Use the common insertion service path
    await window.electronAPI.insertTextDirect(m.content);
    // Reset query and close the floating window
    setQuery('');
    setHighlight(0);
    window.electronAPI.hideMacroSearch?.();
  }, [filtered]);

  const onKeyDown = React.useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(0, h - 1));
    } else if (e.key === 'Enter' || e.key === 'ArrowRight') {
      e.preventDefault();
      insertAtIndex(highlight);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      window.electronAPI.hideMacroSearch?.();
    }
  }, [filtered.length, highlight, insertAtIndex]);

  React.useEffect(() => {
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onKeyDown]);

  if (!isGoldOrEnterprise) {
    return (
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100%',
        padding: 8
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: 8 }}>
            <FaCrown size={28} style={{ color: '#FFD700' }} />
          </div>
          <p style={{ margin: 0, color: '#666', fontSize: 11, lineHeight: 1.3 }}>
            Réservé aux abonnements <br/> Gold et Entreprise
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 10, maxWidth: 236 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <FaSearch />
        <input
          ref={inputRef}
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Rechercher une macro..."
          style={{ flex: 1, height: 30, padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc' }}
        />
      </div>
      <div style={{ maxHeight: 140, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6 }}>
        {filtered.map((m, idx) => (
          <div
            key={`${m.key}-${idx}`}
            onMouseEnter={() => setHighlight(idx)}
            onClick={() => insertAtIndex(idx)}
            style={{
              padding: '6px 10px',
              cursor: 'pointer',
              background: idx === highlight ? 'rgba(220,20,60,0.08)' : 'transparent',
              borderBottom: '1px solid #f2f2f2',
              display: 'flex',
              alignItems: 'center',
              gap: 10
            }}
          >
            <span style={{ fontFamily: 'monospace', color: '#c62828', minWidth: 80, maxWidth: 90, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.key}</span>
            <span style={{ color: '#666', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>{(m.content || '').length > 20 ? `${m.content.slice(0, 20)}…` : m.content}</span>
            {idx === highlight && <FaCheck style={{ color: '#c62828', marginLeft: 'auto' }} />}
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ padding: 10, color: '#999' }}>Aucun résultat</div>
        )}
      </div>
      <div style={{ marginTop: 6, color: '#888', fontSize: 11 }}>
        ↑/↓ naviguer • → insérer • Échap annuler
      </div>
    </div>
  );
}


